/* eslint-disable no-nested-ternary */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  ChevronDown,
  // ChevronRight,
  ChevronUp,
  // ViewportNarrow,
  // ViewportWide,
  Mail,
  Signal,
  // Linkedin,
  Whatsapp,
  Checkbox as CheckboxIcon,
  LinkedinSquare,
  Copy,
  ForwardStep,
  Refresh,
  Alarm,
  Check,
  CircleCheck,
  Link as LinkIcon,
  AlertTriangle,
  Send,
  Plus,
} from '@saleshandy/icons';
import classNames from 'classnames';
import moment from 'moment';
import { Alert, Button, SkeletonLoading } from '@saleshandy/design-system';
import { Spinner } from 'react-bootstrap';
import TaskListSkeleton from './task-list-skeleton';
import { accessibleOnClick } from '../../../../../../../../../../shared/utils/accessible-on-click';
import Avatar from '../../../../../../../../../../shared/design-system/components/atoms/initials-avatar/avatar';
import InlineMentionEditor from '../../../../../../../prospect-kanban/Inline-mention-editor';
import {
  AttachmentType,
  AttachmentUploadStatus,
} from '../../../../../../../../../../shared/editor/types';
import { RequestStatus } from '../../../../../../../../../../shared/enums/request-status';
import {
  CreateProspectNoteProp,
  UpdateProspectNoteProps,
  visibility,
} from '../../../../../../../../types';
import {
  getTaskTitle,
  isWhatsappTask,
  isCallTask,
  getCallOutcomeOptions,
  getCallTaskDesc,
  getCompleteActionDesc,
  getTaskActionTitle,
  getConfirmationModalClass,
  isLinkedInType,
} from '../../../../../../../../../tasks/components/tasks-content/utils/helper';
import { TaskAction } from '../../../../../../../../../tasks/utils/enums';
import { TaskStatusKey } from '../../utils/enums';
import { SequenceProgress, SequenceStepType } from '../../../../../../../../../sequence/enums';
import {
  getButtonLabel,
  getProspectDetails,
} from '../../../../../../../../../tasks/components/tasker-modal/utils/helper';
import Attachment from '../../../../../../../../../../shared/components/attachment';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../../../../../shared/design-system/components/overlay';
import SnoozeModal from '../../../../../../../../../tasks/components/tasks-content/components/snooze-modal';
import ConfirmationModalV3 from '../../../../../../../../../../shared/design-system/components/atoms/confirmation-modal/confirmation-modal-v3';
import Select from '../../../../../../../../../../shared/design-system/components/select';
import {
  getDateColumnCellValue,
  getFormattedSkippedDate,
  getFormattedCompletedDate,
  getFormattedFailedDate,
  getPriorityClassName,
  getTaskMessageLabel,
  getTaskSubjectLabel,
  resolveSequenceStepType,
  resolveTaskTypeKey,
} from '../../utils/helper';
import { Priority } from '../../../../../../../../../../shared/components/priority';
import { getStepPriority } from '../../../../../../../../../sequence/helpers/sequence-step';
import toaster, { Theme } from '../../../../../../../../../../shared/toaster';
import { getIsRequestPending } from '../../../../../../../../../../shared/utils/get-request-status';
import { executeOnRequestStatus } from '../../../../../../../../../../shared/utils';
import TruncatedTextWithAutoTooltip from '../../../../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import GlobalRestrictionModal from '../../../../../../../../../home/components/global-restriction-modal';
import FailedReasonPopup from '../../../../../../../../../tasks/components/tasks-content/components/failed-reason-popup/failed-reason-popup';
import { FailedTaskDetail, FAILED_REASON_LABELS } from '../../../../../../../../../linkedin-automation/types';
import { LinkedInPlatform } from '../../../../../../../../../linkedin-automation/enums';

interface Task {
  id: string | number;
  title?: string | number;
  type?: string | number | SequenceStepType;
  dueAt?: string;
  status?: string;
  priority?: string | number;
  assignee?: {
    firstName?: string;
    lastName?: string;
  };
  taskPayload?: {
    payload?: {
      connectionNote?: string;
      message?: string;
      content?: string;
      subject?: string;
      title?: string;
    };
  };
  sequenceProspectTask?: {
    variant?: {
      sequenceStepVariantAttachments?: Array<{
        id: string | number;
        attachment?: {
          id: string | number;
          name?: string;
          size?: string | number;
          mimeType?: string;
        };
      }>;
    };
  };
  taskAttachments?: Array<{
    id: string | number;
    attachment?: {
      id: number;
      name?: string;
      size?: string | number;
      mimeType?: string;
      s3ObjectKey?: string;
      createdAt?: string;
    };
  }>;
  hashId?: string;
  [key: string]: any;
}

const isLinkedInAutomationType = (type?: string | SequenceStepType) =>
  type === SequenceStepType.AimfoxCampaignPush ||
  type === SequenceStepType.HeyReachCampaignPush;

const LightningBoltIcon: React.FC<{ width?: number; height?: number; className?: string }> = ({
  width = 18,
  height = 18,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const taskTypeIcons = [
  {
    type: 'call',
    icon: Signal,
  },
  {
    type: 'linkedin-automation',
    icon: LightningBoltIcon,
  },
  {
    type: 'linkedin',
    icon: LinkedinSquare,
  },
  {
    type: 'whatsapp',
    icon: Whatsapp,
  },
  {
    type: 'checkbox',
    icon: CheckboxIcon,
  },
  {
    type: 'task',
    icon: CheckboxIcon,
  },
  {
    type: 'email',
    icon: Mail,
  },
];

const getTaskTypeIcon = (type?: string | SequenceStepType) => {
  const normalizedType = resolveTaskTypeKey(type);
  const iconConfig = taskTypeIcons.find((item) => item.type === normalizedType);
  return iconConfig?.icon || CheckboxIcon;
};

const getTaskStatusFromDate = (dueAt?: string): TaskStatusKey => {
  if (!dueAt) return TaskStatusKey.Upcoming;

  const now = moment();
  const dueMoment = moment(dueAt);
  const today = moment().startOf('day');
  const dueDate = moment(dueAt).startOf('day');

  if (dueDate.isSame(today)) {
    return TaskStatusKey.DueToday;
  }

  if (now.isAfter(dueMoment)) {
    return TaskStatusKey.Overdue;
  }

  return TaskStatusKey.Upcoming;
};

type CopiableField = 'subject' | 'message' | 'link';

const getTaskSubject = (task: Task, shareableData?: any) => {
  // Use shareableData if available, otherwise fall back to task
  if (shareableData?.taskPayload?.payload) {
    return (
      shareableData?.taskPayload?.payload?.subject?.trim() ||
      shareableData?.taskPayload?.payload?.title?.trim() ||
      ''
    );
  }
  return (
    task?.taskPayload?.payload?.subject?.trim() ||
    task?.taskPayload?.payload?.title?.trim() ||
    ''
  );
};

const getTaskMessageContent = (task: Task, shareableData?: any) => {
  // Use shareableData if available, otherwise fall back to task
  if (shareableData?.taskPayload?.payload) {
    return (
      shareableData?.taskPayload?.payload?.connectionNote ||
      shareableData?.taskPayload?.payload?.message ||
      shareableData?.taskPayload?.payload?.content ||
      ''
    );
  }
  return (
    task?.taskPayload?.payload?.connectionNote ||
    task?.taskPayload?.payload?.message ||
    task?.taskPayload?.payload?.content ||
    ''
  );
};

const stripHtml = (value?: string) =>
  value
    ? value
        .replace(/<\/?[a-zA-Z][^>]*>/g, '')
        .replace(/\u00A0/g, ' ')
        .trim()
    : '';

// Transform API attachment format to AttachmentType format
const transformApiAttachmentToAttachmentType = (
  apiAttachment: any,
): AttachmentType => {
  const sizeNumber = apiAttachment?.size
    ? parseInt(String(apiAttachment.size), 10)
    : 0;

  return {
    attachmentId: apiAttachment?.id,
    status: AttachmentUploadStatus.Existing,
    file: {
      name: apiAttachment?.name || '',
      type: apiAttachment?.mimeType || '',
      size: sizeNumber,
    },
    attachmentSize: sizeNumber,
  };
};

// Transform API attachments array to AttachmentType array
const transformApiAttachments = (apiAttachments: any[]): AttachmentType[] => {
  if (!apiAttachments || !Array.isArray(apiAttachments)) {
    return [];
  }
  return apiAttachments.map(transformApiAttachmentToAttachmentType);
};

interface TaskListProps {
  tasks?: Task[];
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  isLoading?: boolean;
  onTaskAction?: (
    taskId: number,
    action: TaskAction,
    rescheduledDate?: Date,
    callOutcome?: string,
  ) => void;
  isActionPending?: boolean;
  sendGetShareableTaskerDataRequest?: (hashId: { hashId: string }) => void;
  getShareableTaskerDataRequestStatus?: any;
  shareableTaskerData?: any;
  resetShareableTaskerState?: () => void;
  taskIdFromUrl?: string | null;
  getWhatsappLinkRequest?: (hashId: number) => void;
  getWhatsappLinkRequestStatus?: any;
  getWhatsappLinkResponse?: string;
  resetGetWhatsappLinkState?: () => void;
  setShowGlobalRestrictionModal?: (type: any) => void;
  selectedProspectId?: number;
  createProspectNote?: (payload: CreateProspectNoteProp) => void;
  createProspectNoteRequestStatus?: RequestStatus;
  createProspectNoteRequestMessage?: string;
  createProspectNoteResponse?: any;
  resetCreateProspectNotesListRequests?: () => void;
  updateProspectNote?: (payload: UpdateProspectNoteProps) => void;
  updateProspectNoteRequestStatus?: RequestStatus;
  resetUpdateProspectNoteRequests?: () => void;
  handleSendAttachmentUploadRequest?: (payload: any) => void;
  handleSendDeleteAttachmentRequest?: (payload: any) => void;
  handleResetUploadAttachmentRequest?: () => void;
  handleResetDeleteAttachmentRequest?: () => void;
  uploadAttachmentRequestStatus?: RequestStatus;
  uploadAttachmentRequestError?: any;
  uploadedAttachment?: any;
  deleteAttachmentRequestStatus?: RequestStatus;
  deleteAttachmentRequestError?: any;
  deletedAttachmentId?: number;
  hasMoreTasks?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  updateTaskRequestStatus?: RequestStatus;
  selectedStatus?: TaskStatusKey;
  removeTaskIdFromUrl?: () => void;
  onActivityChange?: (value: boolean) => void;
  onNoteChange?: (value: boolean) => void;
  activeTab?: string;
  onCloseExpandedTasks?: React.MutableRefObject<(() => void) | null>;
  prospectActivityCount?: {
    notes: string | number;
    tasks: string | number;
    emails: string | number;
    sequences: string | number;
  };
  editProspectAcitivityCount?: (payload: any) => void;
  isTaskTabSkeletonLoaded?: boolean;
  isDemoAccount?: boolean;
}

const TaskList: React.FC<TaskListProps> = ({
  tasks,
  scrollContainerRef,
  isLoading,
  onTaskAction,
  isActionPending = false,
  sendGetShareableTaskerDataRequest,
  shareableTaskerData,
  resetShareableTaskerState,
  taskIdFromUrl,
  getWhatsappLinkRequest,
  getWhatsappLinkRequestStatus,
  getWhatsappLinkResponse,
  resetGetWhatsappLinkState,
  // setShowGlobalRestrictionModal,
  selectedProspectId,
  createProspectNote,
  createProspectNoteRequestStatus,
  createProspectNoteRequestMessage,
  createProspectNoteResponse,
  resetCreateProspectNotesListRequests,
  updateProspectNote,
  updateProspectNoteRequestStatus,
  resetUpdateProspectNoteRequests,
  handleSendAttachmentUploadRequest,
  handleSendDeleteAttachmentRequest,
  handleResetUploadAttachmentRequest,
  handleResetDeleteAttachmentRequest,
  uploadAttachmentRequestStatus,
  uploadAttachmentRequestError,
  uploadedAttachment,
  deleteAttachmentRequestStatus,
  deleteAttachmentRequestError,
  deletedAttachmentId,
  hasMoreTasks = false,
  onLoadMore,
  isLoadingMore = false,
  updateTaskRequestStatus,
  selectedStatus,
  removeTaskIdFromUrl,
  onActivityChange,
  onNoteChange,
  activeTab,
  onCloseExpandedTasks,
  prospectActivityCount,
  editProspectAcitivityCount,
  isTaskTabSkeletonLoaded = false,
  isDemoAccount,
}) => {
  // Only show tasks from the listing API call
  // Only merge shareableTaskerData if viewing via URL and task doesn't exist in API tasks
  const resolvedTasks = useMemo(() => {
    const baseTasks = Array.isArray(tasks) ? tasks : [];

    const filterDuplicateTask = taskIdFromUrl
      ? baseTasks?.filter((item) => item?.id !== shareableTaskerData?.id)
      : baseTasks;
    // Only include shareableTaskerData if we're viewing via URL (taskIdFromUrl exists)
    // and the task doesn't already exist in the API tasks
    if (
      taskIdFromUrl &&
      shareableTaskerData &&
      Object.keys(shareableTaskerData)?.length > 0
    ) {
      const { id } = shareableTaskerData;
      const taskIdExist = filterDuplicateTask?.some(
        (item) => String(item?.id) === String(id),
      );
      // Only add shareableTaskerData if the task doesn't exist in API tasks
      if (!taskIdExist) {
        return [shareableTaskerData, ...filterDuplicateTask];
      }
    }

    return filterDuplicateTask;
  }, [tasks, shareableTaskerData, taskIdFromUrl]);

  const [expandedTasks, setExpandedTasks] = useState<string[]>([]);
  const [copiedField, setCopiedField] = useState<{
    taskId: string;
    field: CopiableField;
  } | null>(null);

  useEffect(() => {
    if (onCloseExpandedTasks) {
      onCloseExpandedTasks.current = () => {
        setExpandedTasks([]);
      };
    }
    return () => {
      if (onCloseExpandedTasks) {
        onCloseExpandedTasks.current = null;
      }
    };
  }, [onCloseExpandedTasks]);
  const [locallyDisabledTasks, setLocallyDisabledTasks] = useState<Set<string>>(
    new Set(),
  );
  const listItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const subjectRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const instructionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const prevExpandedTasks = useRef<string[]>([]);
  const isLoadingMoreRef = useRef<boolean>(false);
  const prevActiveTabRef = useRef<string | undefined>(activeTab);
  const justReturnedToTasksTabRef = useRef<boolean>(false);
  const tasksNeedingFreshDataRef = useRef<Set<string>>(new Set());
  const shareableTaskDataMapRef = useRef<Record<string, any>>({});
  const [expandedInstructions, setExpandedInstructions] = useState<Set<string>>(
    new Set(),
  );
  const [showNoteEditor, setShowNoteEditor] = useState<string | null>(null);
  // Tracks which task's note editor is currently in "edit" mode (footer controls visible).
  const [editingNoteTaskId, setEditingNoteTaskId] = useState<string | null>(
    null,
  );
  const [
    instructionsNeedingTruncation,
    setInstructionsNeedingTruncation,
  ] = useState<Set<string>>(new Set());

  // LinkedIn Automation failed reason popup state
  const [failedReasonTask, setFailedReasonTask] = useState<FailedTaskDetail | null>(null);

  // Modal states
  const [isSnoozeModalVisible, setIsSnoozeModalVisible] = useState(false);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(
    false,
  );
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskAction, setTaskAction] = useState<TaskAction | null>(null);
  const [callOutcome, setCallOutcome] = useState<{
    key: string;
    label: string;
  } | null>(null);

  // Store shareable task data per task (keyed by hashId)
  const [shareableTaskDataMap, setShareableTaskDataMap] = useState<
    Record<string, any>
  >({});
  const [loadingShareableTasks, setLoadingShareableTasks] = useState<
    Set<string>
  >(new Set());

  // Note editor state per task (for creating new notes)
  const [taskEditorValues, setTaskEditorValues] = useState<
    Record<string, string>
  >({});
  const [taskVisibilityKeys, setTaskVisibilityKeys] = useState<
    Record<string, string>
  >({});
  const [taskAttachments, setTaskAttachments] = useState<
    Record<string, AttachmentType[]>
  >({});
  const [pendingNoteTaskId, setPendingNoteTaskId] = useState<string | null>(
    null,
  );
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );
  const [
    globalRestrictionModalContent,
    setGlobalRestrictionModalContent,
  ] = useState({
    title: '',
    content: '',
  });

  // Store note data for direct update after create/update
  const [pendingNoteData, setPendingNoteData] = useState<{
    content: string;
    visibility: string;
    attachments: number[];
    attachmentObjects?: any[];
  } | null>(null);

  // Helper function to update shareableTaskDataMap with note data
  const updateShareableTaskDataMapWithNote = useCallback(
    (
      hashId: string,
      noteData: {
        content: string;
        visibility: string;
        attachments: number[];
        attachmentObjects?: any[];
      },
      isCreate: boolean,
      noteId?: number,
    ) => {
      // Get transformed attachments
      const transformedAttachments =
        noteData?.attachmentObjects && noteData?.attachmentObjects?.length > 0
          ? noteData?.attachmentObjects
          : noteData?.attachments?.map((id) => ({
              id,
              name: '',
              size: 0,
              mimeType: '',
            }));

      // Update shareableTaskDataMap with note data using functional update
      setShareableTaskDataMap((prev) => {
        const existingShareableData = prev[hashId] || {};
        const existingNoteResponse = existingShareableData?.noteResponse || {};

        return {
          ...prev,
          [hashId]: {
            ...existingShareableData,
            noteResponse: {
              ...(isCreate ? {} : existingNoteResponse),
              id: noteId || (isCreate ? Date.now() : existingNoteResponse?.id),
              content: noteData.content,
              visibility: noteData.visibility,
              attachments: transformedAttachments,
            },
          },
        };
      });
    },
    [],
  );

  useEffect(() => {
    shareableTaskDataMapRef.current = shareableTaskDataMap;
  }, [shareableTaskDataMap]);

  // Auto-expand task if taskId matches URL parameter and scroll to it
  useEffect(() => {
    if (taskIdFromUrl && resolvedTasks?.length > 0) {
      const matchingTask = resolvedTasks.find(
        (task) => (task as any).hashId === taskIdFromUrl,
      );
      if (matchingTask) {
        const taskIdString = String(matchingTask.id);
        setExpandedTasks((prev) => {
          if (!prev.includes(taskIdString)) {
            const updated = [...prev, taskIdString];
            prevExpandedTasks.current = updated;

            // Scroll to the task after it's expanded (only for URL redirects)
            setTimeout(() => {
              const element = listItemRefs.current[taskIdString];
              const container = scrollContainerRef?.current;
              if (element && container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const offset = 146;
                const scrollOffset =
                  elementRect.top - containerRect.top - offset;

                container.scrollBy({
                  top: scrollOffset,
                  behavior: 'smooth',
                });
              }
            }, 100); // Increased delay to ensure content is loaded

            return updated;
          }
          return prev;
        });
      }
    }
  }, [taskIdFromUrl, resolvedTasks, scrollContainerRef]);

  useEffect(() => {
    setLocallyDisabledTasks((prev) => {
      const next = new Set<string>();
      resolvedTasks.forEach((task) => {
        const id = String(task.id);
        if (prev.has(id)) {
          const hashId = (task as any)?.hashId;
          const shareableData = hashId ? shareableTaskDataMap[hashId] : null;
          const hasCompletedOrSkipped =
            task.completedAt ||
            task.skippedAt ||
            (task as any)?.completedAt ||
            (task as any)?.skippedAt ||
            shareableData?.completedAt ||
            shareableData?.skippedAt;
          if (hasCompletedOrSkipped) {
            next.add(id);
          }
        }
      });

      const isSameSize = next.size === prev.size;
      const hasSameItems =
        isSameSize && Array.from(next).every((id) => prev.has(id));

      return hasSameItems ? prev : next;
    });
  }, [resolvedTasks, shareableTaskDataMap]);

  // Handle shareable task data response
  useEffect(() => {
    if (justReturnedToTasksTabRef.current) {
      if (resetShareableTaskerState) {
        resetShareableTaskerState();
      }
      return;
    }

    if (shareableTaskerData && Object.keys(shareableTaskerData).length > 0) {
      const { hashId } = shareableTaskerData;
      if (hashId) {
        if (tasksNeedingFreshDataRef.current.has(hashId)) {
          tasksNeedingFreshDataRef.current.delete(hashId);
          if (resetShareableTaskerState) {
            resetShareableTaskerState();
          }
          return;
        }

        setShareableTaskDataMap((prev) => ({
          ...prev,
          [hashId]: shareableTaskerData,
        }));
        setLoadingShareableTasks((prev) => {
          const next = new Set(prev);
          next.delete(hashId);
          return next;
        });

        // Initialize taskAttachments with transformed API attachments if they exist
        const { noteResponse } = shareableTaskerData;
        if (noteResponse?.attachments && noteResponse.attachments.length > 0) {
          const task = resolvedTasks?.find(
            (t) => (t as any)?.hashId === hashId,
          );
          if (task) {
            const taskId = String(task.id);
            // Only initialize if taskAttachments[taskId] doesn't exist yet
            setTaskAttachments((prev) => {
              if (!prev[taskId]) {
                return {
                  ...prev,
                  [taskId]: transformApiAttachments(noteResponse.attachments),
                };
              }
              return prev;
            });
          }
        }
      }
      if (resetShareableTaskerState) {
        resetShareableTaskerState();
      }
    }
  }, [shareableTaskerData, resetShareableTaskerState, resolvedTasks]);

  useEffect(() => {
    const prevTab = prevActiveTabRef.current;
    const currentTab = activeTab;

    if (prevTab === 'tasks' && currentTab !== 'tasks') {
      const cachedHashIds = new Set<string>();
      Object.keys(shareableTaskDataMapRef.current).forEach((hashId) => {
        cachedHashIds.add(hashId);
      });
      tasksNeedingFreshDataRef.current = cachedHashIds;

      setShareableTaskDataMap({});
      setLoadingShareableTasks(new Set());
      setExpandedTasks([]);
      if (resetShareableTaskerState) {
        resetShareableTaskerState();
      }
    }

    if (prevTab !== 'tasks' && currentTab === 'tasks') {
      setShareableTaskDataMap({});
      setLoadingShareableTasks(new Set());
      justReturnedToTasksTabRef.current = true;
      if (resetShareableTaskerState) {
        resetShareableTaskerState();
      }
      setTimeout(() => {
        justReturnedToTasksTabRef.current = false;
      }, 2000);
    }

    // Update the ref
    prevActiveTabRef.current = currentTab;
  }, [activeTab, resetShareableTaskerState]);

  useEffect(() => {
    if (activeTab !== 'tasks') {
      return;
    }

    executeOnRequestStatus({
      status: createProspectNoteRequestStatus,
      onSuccess: () => {
        if (resetCreateProspectNotesListRequests) {
          resetCreateProspectNotesListRequests();
        }
        if (createProspectNoteRequestMessage) {
          toaster.success(createProspectNoteRequestMessage, {
            theme: Theme.New,
          });
        }
        // Directly update shareableTaskDataMap instead of calling API
        if (pendingNoteTaskId && pendingNoteData) {
          const task = resolvedTasks?.find(
            (t) => String(t.id) === pendingNoteTaskId,
          );
          const hashId = (task as any)?.hashId;
          if (hashId) {
            // Use the actual note ID from API response instead of Date.now()
            const actualNoteId = createProspectNoteResponse?.id;
            updateShareableTaskDataMapWithNote(
              hashId,
              pendingNoteData,
              true,
              actualNoteId,
            );
          }
        }
        // Clear editor values and pending task ID after successful note creation
        setTaskEditorValues({});
        setTaskAttachments({});
        setPendingNoteTaskId(null);
        setPendingNoteData(null);
        // Remove taskId from URL after note creation
        if (removeTaskIdFromUrl) {
          removeTaskIdFromUrl();
        }
        onActivityChange?.(true);
        onNoteChange?.(true);
      },
      onFailed: () => {
        if (resetCreateProspectNotesListRequests) {
          resetCreateProspectNotesListRequests();
        }
        setPendingNoteTaskId(null);
        setPendingNoteData(null);
      },
    });
  }, [
    createProspectNoteRequestStatus,
    createProspectNoteRequestMessage,
    createProspectNoteResponse,
    resetCreateProspectNotesListRequests,
    pendingNoteTaskId,
    pendingNoteData,
    resolvedTasks,
    updateShareableTaskDataMapWithNote,
    onActivityChange,
    onNoteChange,
    activeTab,
  ]);

  useEffect(() => {
    if (activeTab !== 'tasks') {
      return;
    }

    executeOnRequestStatus({
      status: updateProspectNoteRequestStatus,
      onSuccess: () => {
        if (resetUpdateProspectNoteRequests) {
          resetUpdateProspectNoteRequests();
        }
        toaster.success('Note updated successfully', {
          theme: Theme.New,
        });
        // Directly update shareableTaskDataMap instead of calling API
        if (pendingNoteTaskId && pendingNoteData) {
          const task = resolvedTasks?.find(
            (t) => String(t.id) === pendingNoteTaskId,
          );
          const hashId = (task as any)?.hashId;
          if (hashId) {
            updateShareableTaskDataMapWithNote(hashId, pendingNoteData, false);
          }
        }
        // Clear pending task ID after successful note update
        setPendingNoteTaskId(null);
        setPendingNoteData(null);
        // Remove taskId from URL after note update
        if (removeTaskIdFromUrl) {
          removeTaskIdFromUrl();
        }
        onActivityChange?.(true);
        onNoteChange?.(true);
      },
      onFailed: () => {
        if (resetUpdateProspectNoteRequests) {
          resetUpdateProspectNoteRequests();
        }
        setPendingNoteTaskId(null);
        setPendingNoteData(null);
      },
    });
  }, [
    updateProspectNoteRequestStatus,
    resetUpdateProspectNoteRequests,
    pendingNoteTaskId,
    pendingNoteData,
    resolvedTasks,
    updateShareableTaskDataMapWithNote,
    onActivityChange,
    onNoteChange,
    activeTab,
  ]);

  // Initialize visibility keys for tasks
  useEffect(() => {
    if (resolvedTasks?.length > 0) {
      setTaskVisibilityKeys((prev) => {
        const initialVisibilityKeys: Record<string, string> = {};
        resolvedTasks.forEach((task) => {
          const taskId = String(task.id);
          if (!prev[taskId]) {
            initialVisibilityKeys[taskId] = visibility.Public;
          }
        });
        if (Object.keys(initialVisibilityKeys).length > 0) {
          return { ...prev, ...initialVisibilityKeys };
        }
        return prev;
      });
    }
  }, [resolvedTasks]);

  const handleTaskEditorChange = (taskId: string, value: string) => {
    setTaskEditorValues((prev) => ({
      ...prev,
      [taskId]: value,
    }));
  };

  const handleTaskVisibilityChange = (taskId: string, key: string) => {
    setTaskVisibilityKeys((prev) => ({
      ...prev,
      [taskId]: key,
    }));
  };

  const handleTaskAttachmentsChange = (
    taskId: string,
    attachments: AttachmentType[],
  ) => {
    setTaskAttachments((prev) => ({
      ...prev,
      [taskId]: attachments,
    }));
  };

  const handleTaskAttachmentStatusChange = (
    taskId: string,
    updatedAttachment: AttachmentType,
  ) => {
    setTaskAttachments((prev) => ({
      ...prev,
      [taskId]: (prev[taskId] || []).map((attachment) =>
        attachment.attachmentIdentifier ===
        updatedAttachment.attachmentIdentifier
          ? {
              ...attachment,
              ...updatedAttachment,
              status:
                updatedAttachment.status ||
                AttachmentUploadStatus.UploadSuccess,
            }
          : attachment,
      ),
    }));
  };

  const handleTaskNoteSave = (taskId: string) => {
    // Remove taskId from URL when saving note
    if (removeTaskIdFromUrl) {
      removeTaskIdFromUrl();
    }

    const task = resolvedTasks?.find((t) => String(t.id) === taskId);
    const hashId = (task as any)?.hashId;
    const shareableData = hashId ? shareableTaskDataMap[hashId] : null;
    const noteResponse = shareableData?.noteResponse;

    // Get editor value - prioritize edited value (taskEditorValues), then fallback to noteResponse content
    const editorValue = taskEditorValues[taskId] || noteResponse?.content || '';
    const visibilityKey =
      taskVisibilityKeys[taskId] ||
      noteResponse?.visibility ||
      visibility.Public;

    if (!editorValue?.trim()) return;

    // Collect attachments - prioritize current attachments (taskAttachments), fallback to existing (noteResponse)
    const currentAttachments = taskAttachments[taskId];
    const existingApiAttachments = noteResponse?.attachments || [];

    // Extract attachmentIds from current attachments (AttachmentType format)
    const currentAttachmentIds = currentAttachments
      ? currentAttachments
          .map((attachment) => attachment?.attachmentId)
          .filter((id): id is number => id !== undefined && id !== null)
      : [];

    // Extract attachmentIds from existing API attachments (API format with 'id' field)
    const existingAttachmentIds = existingApiAttachments
      .map((attachment) => attachment?.id)
      .filter((id): id is number => id !== undefined && id !== null);

    // Use current attachments if available, otherwise use existing attachments
    const attachmentIds =
      currentAttachments !== undefined
        ? currentAttachmentIds
        : existingAttachmentIds;

    // Store taskId and note data for direct update after success
    setPendingNoteTaskId(taskId);
    // Get attachment objects from current attachments or existing API attachments
    let attachmentObjects: any[];
    if (currentAttachments !== undefined) {
      // Use current attachments (even if empty array after deletion)
      attachmentObjects = currentAttachments?.map((att) => ({
        id: att.attachmentId,
        name: att.file?.name || '',
        size: att.file?.size || att.attachmentSize || 0,
        mimeType: att.file?.type || '',
      }));
    } else if (existingApiAttachments?.length > 0) {
      attachmentObjects = existingApiAttachments;
    } else {
      attachmentObjects = attachmentIds?.map((id) => ({
        id,
        name: '',
        size: 0,
        mimeType: '',
      }));
    }
    setPendingNoteData({
      content: editorValue,
      visibility: visibilityKey,
      attachments: attachmentIds,
      attachmentObjects,
    });

    // If note exists, update it; otherwise create new note
    if (noteResponse?.id) {
      updateProspectNote({
        noteId: noteResponse.id,
        content: editorValue,
        visibility: visibilityKey as visibility,
        attachmentIds: attachmentIds || [],
      });
    } else if (selectedProspectId) {
      const taskIdNumber = task ? Number(task.id) : undefined;
      createProspectNote({
        prospectId: selectedProspectId,
        content: editorValue,
        visibility: visibilityKey as visibility,
        taskId: taskIdNumber,
        attachmentIds: attachmentIds || [],
      });
    }
  };

  const toggleTaskExpansion = (taskId: string) => {
    // Remove taskId from URL when expanding/collapsing tasks
    if (removeTaskIdFromUrl) {
      removeTaskIdFromUrl();
    }
    setExpandedTasks((prev) => {
      const isExpanding = !prev.includes(taskId);
      if (isExpanding) {
        // When expanding, fetch shareable task data if not already loaded
        const task = resolvedTasks?.find((t) => String(t.id) === taskId);
        const hashId = (task as any)?.hashId;
        const needsFreshData = tasksNeedingFreshDataRef.current.has(hashId);
        const shouldFetch =
          hashId &&
          sendGetShareableTaskerDataRequest &&
          (!shareableTaskDataMap[hashId] ||
            justReturnedToTasksTabRef.current ||
            needsFreshData) &&
          !loadingShareableTasks.has(hashId);
        if (shouldFetch) {
          if (needsFreshData) {
            tasksNeedingFreshDataRef.current.delete(hashId);
          }
          justReturnedToTasksTabRef.current = false;
          setLoadingShareableTasks((prevSet) => {
            const next = new Set(prevSet);
            next.add(hashId);
            return next;
          });
          sendGetShareableTaskerDataRequest({ hashId });
        }
        return [...prev, taskId];
      }
      return prev.filter((id) => id !== taskId);
    });
  };

  const toggleInstructionExpansion = useCallback(
    (taskId: string) => {
      // Remove taskId from URL when toggling instruction expansion
      if (removeTaskIdFromUrl) {
        removeTaskIdFromUrl();
      }
      setExpandedInstructions((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(taskId)) {
          newSet.delete(taskId);
        } else {
          newSet.add(taskId);
        }
        return newSet;
      });
    },
    [removeTaskIdFromUrl],
  );

  const markTaskLocallyDisabled = (taskId: string | number) => {
    setLocallyDisabledTasks((prev) => {
      const next = new Set(prev);
      next.add(String(taskId));
      return next;
    });
  };

  // const handleExpandAll = () => {
  //   if (expandedTasks.length === resolvedTasks?.length) {
  //     setExpandedTasks([]);
  //   } else {
  //     setExpandedTasks(resolvedTasks?.map((task) => String(task.id)) || []);
  //   }
  // };

  const handleCopyContent = async (taskId: string, field: CopiableField) => {
    // Remove taskId from URL when copying content
    if (removeTaskIdFromUrl) {
      removeTaskIdFromUrl();
    }

    if (field === 'link') {
      // Handle task link copying
      const task = resolvedTasks?.find((t) => String(t.id) === taskId);
      if (!task) return;

      const baseUrl = window?.location?.origin;
      const hashId = (task as any).hashId || taskId;
      const taskLink = `${baseUrl}/crm/kanban/${task?.prospect?.id}/tasks?taskId=${hashId}`;

      if (
        typeof navigator === 'undefined' ||
        !navigator?.clipboard?.writeText
      ) {
        console.error('Clipboard API is not supported in this environment.');
        return;
      }

      try {
        await navigator.clipboard.writeText(taskLink);
        setCopiedField({ taskId, field });
        setTimeout(() => {
          setCopiedField((prev) =>
            prev?.taskId === taskId && prev?.field === field ? null : prev,
          );
        }, 2000);
      } catch (err) {
        console.error('Failed to copy task link:', err);
      }
      return;
    }

    // Handle subject and message copying
    const refsMap =
      field === 'subject' ? subjectRefs.current : messageRefs.current;
    const targetElement = refsMap[taskId];
    if (!targetElement) {
      return;
    }

    const contentToCopy = targetElement.textContent?.trim();
    if (!contentToCopy) {
      return;
    }

    if (typeof navigator === 'undefined' || !navigator?.clipboard?.writeText) {
      console.error('Clipboard API is not supported in this environment.');
      return;
    }

    try {
      await navigator.clipboard.writeText(contentToCopy);
      setCopiedField({ taskId, field });
      setTimeout(() => {
        setCopiedField((prev) =>
          prev?.taskId === taskId && prev?.field === field ? null : prev,
        );
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const isFieldCopied = (taskId: string, field: CopiableField) =>
    copiedField?.taskId === taskId && copiedField?.field === field;

  const renderAttachments = (task: Task, shareableData?: any) => {
    // Use attachments from shareableData if available, otherwise use task
    const attachments = shareableData?.taskAttachments;

    if (!attachments || attachments.length === 0) {
      return null;
    }

    return (
      <div className="task-attachments mt-3">
        <div className="d-flex flex-wrap gap-4">
          {attachments?.map((attachmentData) => (
            <Attachment
              key={attachmentData?.id}
              attachment={{
                attachmentId: attachmentData?.attachment?.id,
                file: {
                  name: attachmentData?.attachment?.name,
                  size: attachmentData?.attachment?.size,
                  type: attachmentData?.attachment?.mimeType,
                },
                attachmentSize: Number(attachmentData?.attachment?.size),
                status: AttachmentUploadStatus.Existing,
              }}
              showAttachmentDeleteButton={false}
            />
          ))}
        </div>
      </div>
    );
  };

  // LinkedIn button handler
  const sendLinkedinRequest = (task: Task, shareableData?: any) => {
    // if (getIsDemoAccount()) {
    //   if (setShowGlobalRestrictionModal) {
    //     setShowGlobalRestrictionModal(task?.type);
    //   }
    //   return null;
    // }

    // Use prospect data from shareableData if available, otherwise use task
    const prospectAttributes = shareableData?.prospect?.attributes
      ? shareableData.prospect.attributes
      : (task as any)?.prospect?.attributes;
    const prospectDetails = getProspectDetails(prospectAttributes);

    if (!prospectDetails?.LinkedIn) {
      return toaster.error('LinkedIn profile not found', { theme: Theme.New });
    }

    return window.open(prospectDetails?.LinkedIn, '_blank');
  };

  // WhatsApp button handler
  const sendWhatsAppRequest = (task: Task) => {
    const hashId = (task as any)?.hashId;
    if (hashId && getWhatsappLinkRequest) {
      getWhatsappLinkRequest(hashId);
    }

    // if (getIsDemoAccount()) {
    //   if (setShowGlobalRestrictionModal) {
    //     setShowGlobalRestrictionModal(task?.type);
    //   }
    //   return null;
    // }
  };

  // Handle WhatsApp link response
  useEffect(() => {
    executeOnRequestStatus({
      status: getWhatsappLinkRequestStatus,
      onSuccess: () => {
        if (getWhatsappLinkResponse) {
          const replaceNbspWithSpaces = (str: string) => {
            if (!str) return str;
            return str.replace(/(&nbsp;)+/g, (match) =>
              ' '.repeat(match.length / 6),
            );
          };
          const decodedWhatsappLinkResponse = replaceNbspWithSpaces(
            decodeURIComponent(getWhatsappLinkResponse),
          );
          window?.open(decodedWhatsappLinkResponse, '_blank');
        }
        if (resetGetWhatsappLinkState) {
          resetGetWhatsappLinkState();
        }
      },
    });
  }, [
    getWhatsappLinkRequestStatus,
    getWhatsappLinkResponse,
    resetGetWhatsappLinkState,
  ]);

  // Render WhatsApp button with proper logic
  const renderWhatsAppButton = (
    task: Task,
    shareableData?: any,
    isDisabled?: boolean,
  ) => {
    const taskType =
      resolveSequenceStepType(task.type) ?? SequenceStepType.Custom;
    if (!isWhatsappTask(taskType)) {
      return null;
    }

    // Use prospect data from shareableData if available, otherwise use task
    const prospectAttributes = shareableData?.prospect?.attributes
      ? shareableData.prospect.attributes
      : (task as any)?.prospect?.attributes;
    const prospectDetails = getProspectDetails(prospectAttributes);
    const hasPhoneNumber = prospectDetails?.['Phone Number'];

    // Use message content from shareableData if available, otherwise use task
    const messageContent = getTaskMessageContent(task, shareableData);
    const messageLength = messageContent?.length || 0;
    const isMessageTooLong = messageLength > 1900;
    const isLoading = getIsRequestPending(getWhatsappLinkRequestStatus);

    const buttonProps = {
      onClick: () => sendWhatsAppRequest(task),
      disabled: !hasPhoneNumber || isDisabled,
      isLoading,
    };

    if (!hasPhoneNumber) {
      return (
        <OverlayTooltip
          text="WhatsApp number is not available"
          placement={Placement.Top}
          className="open-whatsapp-btn"
        >
          <Button {...buttonProps}>Open WhatsApp</Button>
        </OverlayTooltip>
      );
    }

    if (isMessageTooLong) {
      return (
        <OverlayTooltip
          text="Message too long. Please copy it manually to paste fully in WhatsApp."
          placement={Placement.Top}
        >
          <Button {...buttonProps}>Open WhatsApp</Button>
        </OverlayTooltip>
      );
    }

    return <Button {...buttonProps}>Open WhatsApp</Button>;
  };

  // Task action handlers
  const handleTaskAction = (
    task: Task,
    action: TaskAction,
    isDisabled = false,
  ) => {
    if (isDisabled) return;
    // Remove taskId from URL when any task action is triggered
    if (removeTaskIdFromUrl) {
      removeTaskIdFromUrl();
    }
    // Reset call outcome when starting a new action
    setCallOutcome(null);
    setSelectedTask(task);
    setTaskAction(action);

    if (action === TaskAction.SnoozeTask) {
      setIsSnoozeModalVisible(true);
      setIsConfirmationModalVisible(false);
    } else if (action === TaskAction.SkipTask) {
      // Skip action: Call API directly without modal
      if (onTaskAction) {
        const taskId = Number(task.id);
        onTaskAction(taskId, TaskAction.SkipTask);
        markTaskLocallyDisabled(taskId);
      }
      setSelectedTask(null);
      setTaskAction(null);
      editProspectAcitivityCount({
        ...prospectActivityCount,
        tasks:
          Number(prospectActivityCount?.tasks) >= 0
            ? (Number(prospectActivityCount?.tasks) || 0) - 1
            : 1,
      });
    } else if (action === TaskAction.ExecuteTask) {
      // Execute action: Show modal only for call tasks, otherwise call API directly
      editProspectAcitivityCount({
        ...prospectActivityCount,
        tasks:
          Number(prospectActivityCount?.tasks) >= 0
            ? (Number(prospectActivityCount?.tasks) || 0) - 1
            : 1,
      });
      const taskType =
        resolveSequenceStepType(task.type) ?? SequenceStepType.Custom;
      if (isCallTask(taskType)) {
        // For call tasks, show modal to select call outcome
        setIsConfirmationModalVisible(true);
        setIsSnoozeModalVisible(false);
      } else {
        // For non-call tasks, call API directly
        if (onTaskAction) {
          const taskId = Number(task.id);
          onTaskAction(taskId, TaskAction.ExecuteTask);
          markTaskLocallyDisabled(taskId);
        }
        setSelectedTask(null);
        setTaskAction(null);
      }
    }
  };

  const handleSnooze = (date: Date) => {
    if (!selectedTask || !onTaskAction) return;
    const taskId = Number(selectedTask.id);
    onTaskAction(taskId, TaskAction.SnoozeTask, date);
    setIsSnoozeModalVisible(false);
    setSelectedTask(null);
    setTaskAction(null);
  };

  const handleConfirm = () => {
    if (!selectedTask || taskAction === null || !onTaskAction) return;
    const taskId = Number(selectedTask.id);
    const taskType =
      resolveSequenceStepType(selectedTask.type) ?? SequenceStepType.Custom;
    const shouldSendCallOutcome =
      isCallTask(taskType) &&
      taskAction === TaskAction.ExecuteTask &&
      callOutcome?.key;

    onTaskAction(
      taskId,
      taskAction,
      undefined,
      shouldSendCallOutcome ? callOutcome.key : undefined,
    );
    markTaskLocallyDisabled(taskId);
    setIsConfirmationModalVisible(false);
    setSelectedTask(null);
    setTaskAction(null);
    setCallOutcome(null);
  };

  const getModalContent = () => {
    if (!selectedTask || taskAction === null) return null;

    if (taskAction === TaskAction.ExecuteTask) {
      const taskType =
        resolveSequenceStepType(selectedTask.type) ?? SequenceStepType.Custom;
      if (isCallTask(taskType)) {
        return (
          <>
            <div className="d-flex flex-column gap-16">
              <div className="regular-2 popover-arrow-color-txt line-height-24">
                <span>You're about to mark task as complete.</span>
              </div>
              <span className="regular-2 popover-arrow-color-txt complete-task-description line-height-24">
                {getCallTaskDesc(1)}
              </span>
            </div>
            <div className="mt-3">
              <Select<{ key: string; label: string }>
                options={getCallOutcomeOptions}
                placeholder="Select call outcome"
                selectedOptionKey={callOutcome?.key}
                onChange={([option]) => setCallOutcome(option)}
                optionRenderer={(option) => <span>{option?.label}</span>}
                selectedOptionRenderer={([option]) => (
                  <span>{option?.label}</span>
                )}
              />
            </div>
          </>
        );
      }
      return (
        <span className="regular-2 popover-arrow-color-txt mb-3">
          {getCompleteActionDesc(1)}
        </span>
      );
    }

    if (taskAction === TaskAction.SkipTask) {
      return <p>Are you sure you want to skip this task?</p>;
    }

    return null;
  };

  const isButtonDisabled = () => {
    if (!selectedTask || taskAction === null) return true;
    const taskType =
      resolveSequenceStepType(selectedTask.type) ?? SequenceStepType.Custom;
    return (
      !callOutcome &&
      isCallTask(taskType) &&
      taskAction === TaskAction.ExecuteTask
    );
  };

  const handleDiscard = (taskId: string, hasExistingNote: boolean) => {
    setTaskEditorValues((prev) => {
      const updated = { ...prev };
      delete updated[taskId];
      return updated;
    });
    setEditingNoteTaskId(null);
    // If there's no existing note, hide the editor completely.
    // If a note exists, keep it visible but switch back to view mode.
    if (!hasExistingNote) {
      setShowNoteEditor(null);
    }
  };

  // Update ref when isLoadingMore changes
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    // Don't trigger load more if there are no tasks (filter change scenario)
    if (!resolvedTasks || resolvedTasks.length === 0) return;

    // Prevent duplicate calls using ref (faster than state)
    if (isLoadingMoreRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Load more when the user is 100px from the bottom
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      if (hasMoreTasks && !isLoadingMore && onLoadMore) {
        isLoadingMoreRef.current = true; // Set immediately to prevent duplicate calls
        onLoadMore();
      }
    }
  }, [
    hasMoreTasks,
    isLoadingMore,
    onLoadMore,
    scrollContainerRef,
    resolvedTasks,
  ]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, scrollContainerRef]);

  // Check if instruction content needs truncation (2 lines)
  useEffect(() => {
    if (!resolvedTasks || resolvedTasks.length === 0) {
      return;
    }

    const checkTruncation = () => {
      const needsTruncation = new Set<string>();
      Object.entries(instructionRefs.current).forEach(([taskId, element]) => {
        if (element) {
          // Check if content height exceeds 2 lines (line-height 20px)
          // scrollHeight gives the full content height regardless of overflow/clamp
          const maxHeight = 40; // 2 lines * 20px line-height

          // Check if there's meaningful content (not just empty text)
          const hasContent =
            element.textContent && element.textContent.trim().length > 0;

          // Check if content exceeds max height (regardless of expansion state)
          // We need to track this so we can show "See Less" when expanded
          if (hasContent && element.scrollHeight > maxHeight) {
            needsTruncation.add(taskId);
          }
        }
      });
      setInstructionsNeedingTruncation(needsTruncation);
    };

    // Delay to ensure DOM is rendered with content
    const timeoutId = setTimeout(checkTruncation, 100);

    // Also check after content loads
    const longerTimeoutId = setTimeout(checkTruncation, 500);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(longerTimeoutId);
    };
  }, [resolvedTasks, expandedInstructions, expandedTasks]);

  // Show skeleton on initial load or filter change (not during infinite scroll)
  // Show skeleton if loading and not loading more, or if tasks are empty and loading
  if (isLoading && !isLoadingMore && !isTaskTabSkeletonLoaded) {
    return <TaskListSkeleton count={4} />;
  }

  const shouldHideActions =
    selectedStatus === TaskStatusKey.Completed ||
    selectedStatus === TaskStatusKey.Skipped;

  return (
    <div className="task-list-container">
      <div className="task-list-header">
        {/* {resolvedTasks?.length > 0 && (
          <div
            className="task-list-header-actions"
            {...accessibleOnClick(handleExpandAll)}
          >
            <span className="task-list-header-actions-icon">
              {expandedTasks.length === resolvedTasks?.length ? (
                <ViewportNarrow />
              ) : (
                <ViewportWide />
              )}
            </span>
            <span>
              {expandedTasks.length === resolvedTasks?.length
                ? 'Collapse All'
                : 'Expand All'}
            </span>
          </div>
        )} */}
      </div>
      <div className="task-list">
        {resolvedTasks?.map((task) => {
          const taskId = String(task.id);
          const isExpanded = expandedTasks.includes(taskId);

          // Get shareable data for this task
          const hashId = (task as any)?.hashId;
          const shareableData = hashId ? shareableTaskDataMap[hashId] : null;
          // Check if shareable API is loading for this specific task
          const isShareableApiLoading =
            hashId && loadingShareableTasks.has(hashId);
          const isTaskAborted = Boolean(
            shareableData?.isAborted || (task as any)?.isAborted,
          );
          const isTaskCompletedOrSkipped = Boolean(
            shareableData?.completedAt ||
              shareableData?.skippedAt ||
              (task as any)?.completedAt ||
              (task as any)?.skippedAt ||
              locallyDisabledTasks.has(taskId),
          );
          const isTaskActionDisabled =
            isTaskCompletedOrSkipped || isTaskAborted;

          // Use task type from shareable API if available, otherwise use regular task data
          const taskType = shareableData?.type || task.type;
          const TaskIcon = getTaskTypeIcon(taskType);
          const sequenceStepType =
            resolveSequenceStepType(taskType) ?? SequenceStepType.Custom;
          const isWhatsappType = isWhatsappTask(sequenceStepType);
          const isAutomationTask = isLinkedInAutomationType(taskType);
          const isDraftSequence =
            (task as any)?.isDraftSequence ||
            (task as any)?.sequence?.status === 'DRAFT' ||
            (task as any)?.sequence?.progress === SequenceProgress.Draft;

          // Get skippedAt or completedAt from shareableData or task
          const skippedAt = task?.skippedAt;
          const completedAt = task?.completedAt;
          const taskApiStatus = (task as any)?.status;

          // Determine formatted date: prioritize skippedAt > failed > completed > dueAt
          let formattedDate = '';
          let taskStatus = TaskStatusKey.Upcoming;

          if (skippedAt) {
            formattedDate = getFormattedSkippedDate(skippedAt);
            taskStatus = TaskStatusKey.Skipped;
          } else if (completedAt && taskApiStatus === 2) {
            formattedDate = getFormattedFailedDate(completedAt);
            taskStatus = TaskStatusKey.Failed;
          } else if (completedAt) {
            formattedDate = getFormattedCompletedDate(completedAt);
            taskStatus = TaskStatusKey.Completed;
          } else if (task.dueAt) {
            taskStatus = getTaskStatusFromDate(task.dueAt);
            formattedDate = getDateColumnCellValue(taskStatus, task.dueAt);
          }

          const taskSubject = getTaskSubject(task, shareableData);
          const messageContent = getTaskMessageContent(task, shareableData);
          const messagePlainText =
            stripHtml(messageContent) || messageContent?.trim() || '';
          const hasMessageContent = Boolean(messagePlainText);
          const shouldRenderMessageAsHtml =
            isWhatsappType || /<\/?[a-zA-Z][^>]*>/i.test(messageContent || '');

          // Check if there are attachments
          const hasAttachments = Boolean(
            shareableData?.taskAttachments?.length,
          );

          if (!taskSubject) {
            subjectRefs.current[taskId] = null;
          }

          return (
            <div
              ref={(el) => {
                listItemRefs.current[taskId] = el;
              }}
              key={taskId}
              className={classNames('task-list-item', {
                expanded: isExpanded,
                collapsed: !isExpanded,
              })}
            >
              <div
                className={classNames('task-list-item-header', {
                  'task-list-item-header-collapsed': !isExpanded,
                })}
                {...accessibleOnClick(() => toggleTaskExpansion(taskId))}
              >
                <div className="task-list-item-left">
                  <TaskIcon height={20} width={20} color="#9ca3af" />

                  <div className="d-flex align-items-center gap-8">
                    <span className="task-list-item-title">
                      {getTaskTitle(task.type as SequenceStepType, task.type)}
                    </span>

                    {isDraftSequence && isAutomationTask && (
                      <OverlayTooltip
                        text="This task belongs to a draft sequence. Task will run only after sequence is activated."
                        placement={Placement.Bottom}
                      >
                        <span className="task-list-item-lock-icon">
                          <svg width="14" height="17" viewBox="0 0 16 20" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M3 9.99251C2.44772 9.99251 2 10.4402 2 10.9925V16.9925C2 17.5448 2.44772 17.9925 3 17.9925H13C13.5523 17.9925 14 17.5448 14 16.9925V10.9925C14 10.4402 13.5523 9.99251 13 9.99251H3ZM0 10.9925C0 9.33566 1.34315 7.99251 3 7.99251H13C14.6569 7.99251 16 9.33566 16 10.9925V16.9925C16 18.6494 14.6569 19.9925 13 19.9925H3C1.34315 19.9925 0 18.6494 0 16.9925V10.9925Z" fill="currentColor" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M6 14C6 12.8954 6.89543 12 8 12C9.10457 12 10 12.8954 10 14C10 15.1046 9.10457 16 8 16C6.89543 16 6 15.1046 6 14Z" fill="currentColor" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M8 2C7.20435 2 6.44129 2.31607 5.87868 2.87868C5.31607 3.44129 5 4.20435 5 5V9C5 9.55229 4.55228 10 4 10C3.44772 10 3 9.55229 3 9V5C3 3.67392 3.52678 2.40215 4.46447 1.46447C5.40215 0.526784 6.67392 0 8 0C9.32608 0 10.5979 0.526784 11.5355 1.46447C12.4732 2.40215 13 3.67392 13 5V9C13 9.55229 12.5523 10 12 10C11.4477 10 11 9.55229 11 9V5C11 4.20435 10.6839 3.44129 10.1213 2.87868C9.55871 2.31607 8.79565 2 8 2Z" fill="currentColor" />
                          </svg>
                        </span>
                      </OverlayTooltip>
                    )}

                    {task?.sequence && (
                      <div className="d-flex align-items-center gap-4 sequence-name">
                        <Send width={14} height={14} color="#6b7280" />
                        <div className="sequence-title">
                          <TruncatedTextWithAutoTooltip
                            text={task?.sequence?.title}
                          />
                        </div>
                      </div>
                    )}

                    {task.priority !== undefined && task.priority !== null && (
                      <div className="task-priority">
                        {typeof task.priority === 'number' ? (
                          <Priority {...getStepPriority(task.priority)} />
                        ) : (
                          <div
                            className={classNames('task-priority__item', {
                              [getPriorityClassName(
                                String(task.priority),
                              )]: true,
                            })}
                          >
                            {task.priority}
                          </div>
                        )}
                      </div>
                    )}

                    {isExpanded && (
                      <div
                        className="task-list-item-link-icon cursor-pointer"
                        {...accessibleOnClick((e) => {
                          e.stopPropagation();
                          handleCopyContent(taskId, 'link');
                        })}
                      >
                        {isFieldCopied(taskId, 'link') ? (
                          <OverlayTooltip
                            placement={Placement.Bottom}
                            text="Link Copied"
                          >
                            <div>
                              <Check height={14} width={14} />
                            </div>
                          </OverlayTooltip>
                        ) : (
                          <OverlayTooltip
                            placement={Placement.Bottom}
                            text="Copy Task Link"
                          >
                            <div className="task-list-item-link">
                              <LinkIcon height={14} width={14} />
                            </div>
                          </OverlayTooltip>
                        )}
                      </div>
                    )}
                  </div>
                  {/* )} */}
                </div>
                <div className="task-list-item-right">
                  <>
                    {formattedDate && (
                      <span
                        className={classNames('task-list-item-time', {
                          'due-today': taskStatus === TaskStatusKey.DueToday,
                          'due-upcoming': taskStatus === TaskStatusKey.Upcoming,
                          'due-overdue': taskStatus === TaskStatusKey.Overdue,
                          'task-skipped': taskStatus === TaskStatusKey.Skipped,
                          'task-completed': taskStatus === TaskStatusKey.Completed,
                          'task-failed': taskStatus === TaskStatusKey.Failed,
                        })}
                      >
                        {formattedDate}
                      </span>
                    )}
                  </>

                  <div className="d-flex align-items-center gap-10">
                    {task.assignee && (
                      <OverlayTooltip
                        text={`${task.assignee.firstName} ${task.assignee.lastName}`}
                        placement={Placement.Bottom}
                      >
                        <div className="d-flex align-items-center">
                          <Avatar
                            firstName={task.assignee.firstName || ''}
                            lastName={task.assignee.lastName || ''}
                          />
                        </div>
                      </OverlayTooltip>
                    )}

                    <span className="task-list-item-expand-icon">
                      {isExpanded ? (
                        <ChevronUp height={16} width={16} color="#6b7280" />
                      ) : (
                        <ChevronDown height={16} width={16} color="#6b7280" />
                      )}
                    </span>
                  </div>
                  {/* )} */}
                </div>
              </div>

              {isExpanded && (
                <div className="task-list-item-content d-flex flex-column gap-16">
                  {(() => {
                    // Use prospect data from shareableData if available, otherwise use task
                    const prospectAttributes = shareableData?.prospect
                      ?.attributes
                      ? shareableData.prospect.attributes
                      : (task as any)?.prospect?.attributes;
                    const prospectDetails = getProspectDetails(
                      prospectAttributes,
                    );

                    return (
                      <>
                        {(() => {
                          if (!isAutomationTask) return null;
                          const liError = shareableData?.linkedInTaskError || (task as any)?.linkedInTaskError;
                          const resolvedFailedReason = (task as any)?.failedReason || liError?.errorCode;
                          const campaignName = (task as any).campaignName || (task as any)?.linkedInStepConfig?.campaignName;
                          if (!campaignName && !resolvedFailedReason) return null;
                          return (
                            <div className="d-flex">
                              {campaignName && (
                                <div className="task-list-item-campaign-info" style={{ minWidth: '280px' }}>
                                  <span className="task-list-item-campaign-label regular-1 gray-txt-11">Campaign Name</span>
                                  <span className="task-list-item-campaign-value semibold-2 gray-txt-15">{campaignName}</span>
                                </div>
                              )}
                              {resolvedFailedReason && (
                                <div className="task-list-item-error-reason">
                                  <span className="task-list-item-campaign-label regular-1 gray-txt-11">Error Reason</span>
                                  <button
                                    type="button"
                                    className="task-list-item-error-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFailedReasonTask({
                                        id: Number(task.id),
                                        type: Number(shareableData?.type || task.type),
                                        platform: (task as any).platform || liError?.platform || (task as any)?.linkedInStepConfig?.platform || LinkedInPlatform.Aimfox,
                                        title: String(task.title || ''),
                                        failedReason: resolvedFailedReason,
                                        campaignName: (task as any).campaignName || shareableData?.linkedInStepConfig?.campaignName || (task as any)?.linkedInStepConfig?.campaignName || '',
                                        isAutomated: true,
                                        completedAt: '',
                                        prospectName: '',
                                        prospectEmail: '',
                                        source: '',
                                        sourceStep: '',
                                        taskOwner: '',
                                        taskOwnerInitials: '',
                                        prospectId: selectedProspectId,
                                        sequenceId: (task as any)?.sequence?.id,
                                        missingFields: (task as any).missingFields || liError?.missingFields,
                                        fieldMappings: (task as any).fieldMappings || shareableData?.linkedInStepConfig?.fieldMappings || liError?.fieldMappings,
                                        currentLinkedInUrl: (task as any).currentLinkedInUrl || liError?.currentLinkedInUrl,
                                        rawApiError: (task as any).rawApiError || liError?.errorMessage,
                                      });
                                    }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                      <circle cx="12" cy="12" r="10" />
                                      <line x1="12" y1="8" x2="12" y2="12" />
                                      <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    {FAILED_REASON_LABELS[resolvedFailedReason] || resolvedFailedReason}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {isLinkedInType(sequenceStepType) &&
                          !isShareableApiLoading &&
                          !prospectDetails?.LinkedIn && (
                            <Alert
                              variant="warning"
                              description="LinkedIn URL is invalid / not available"
                              icon={AlertTriangle}
                              className="task-linkedin-whatsapp-banner"
                            />
                          )}

                        {isWhatsappTask(sequenceStepType) &&
                          !isShareableApiLoading &&
                          !prospectDetails?.['Phone Number'] && (
                            <Alert
                              variant="warning"
                              description="Whatsapp number is not available"
                              icon={AlertTriangle}
                              className="task-linkedin-whatsapp-banner"
                            />
                          )}
                      </>
                    );
                  })()}

                  {(() => {
                    const hashId = (task as any)?.hashId;
                    const shareableData = hashId
                      ? shareableTaskDataMap[hashId]
                      : null;
                    const instructionText =
                      shareableData?.instruction || task.instruction;
                    const isInstructionLoading =
                      hashId && loadingShareableTasks.has(hashId);

                    return (
                      !isAutomationTask &&
                      instructionText &&
                      instructionText.trim().length > 0 && (
                        <div className="task-list-item-instructions-wrapper d-flex flex-column gap-4">
                          {isInstructionLoading ? (
                            <SkeletonLoading width={123} height={18} />
                          ) : (
                            <span className="semibold-1 gray-txt-15">
                              Task Instruction
                            </span>
                          )}
                          {isInstructionLoading ? (
                            <div className="d-flex flex-column gap-4">
                              <SkeletonLoading
                                width={454}
                                height={14}
                                style={{ marginTop: '4px' }}
                              />
                              <SkeletonLoading width={388} height={14} />
                              <SkeletonLoading width={240} height={14} />
                            </div>
                          ) : (
                            <>
                              <div
                                ref={(el) => {
                                  instructionRefs.current[taskId] = el;
                                }}
                                className={classNames(
                                  'task-list-item-instructions',
                                  {
                                    'task-list-item-instructions-truncated': !expandedInstructions.has(
                                      taskId,
                                    ),
                                  },
                                )}
                              >
                                {stripHtml(instructionText)}
                              </div>
                              {instructionsNeedingTruncation.has(taskId) && (
                                <button
                                  type="button"
                                  className="task-list-item-instruction-see-more-btn"
                                  onClick={() =>
                                    toggleInstructionExpansion(taskId)
                                  }
                                  {...accessibleOnClick(() =>
                                    toggleInstructionExpansion(taskId),
                                  )}
                                >
                                  {expandedInstructions.has(taskId)
                                    ? 'See Less'
                                    : 'See More'}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )
                    );
                  })()}

                  {(taskSubject && taskSubject.trim().length > 0) ||
                  isShareableApiLoading ? (
                    <div className="d-flex flex-column gap-4">
                      <div className="d-flex align-items-center justify-content-between">
                        {isShareableApiLoading ? (
                          <SkeletonLoading width={123} height={16} />
                        ) : (
                          <span className="semibold-1 gray-txt-15">
                            {getTaskSubjectLabel(sequenceStepType)}
                          </span>
                        )}
                      </div>
                      <div className="task-list-item-subject-wrapper">
                        <div
                          ref={(el) => {
                            subjectRefs.current[taskId] = el;
                          }}
                          className="task-list-item-subject regular-2 popover-arrow-color-txt"
                        >
                          {isShareableApiLoading ? (
                            <div className="d-flex flex-column gap-4">
                              <SkeletonLoading width={536} height={14} />
                              <SkeletonLoading width={423} height={14} />
                            </div>
                          ) : (
                            taskSubject &&
                            taskSubject.trim().length > 0 && <>{taskSubject}</>
                          )}
                        </div>
                        {!isShareableApiLoading && (
                          <div className="task-list-item-copy-icon-static d-flex align-items-center justify-content-end">
                            <OverlayTooltip
                              text={
                                isFieldCopied(taskId, 'subject')
                                  ? 'Copied'
                                  : 'Copy'
                              }
                              placement={Placement.Bottom}
                            >
                              <div
                                {...accessibleOnClick(() =>
                                  handleCopyContent(taskId, 'subject'),
                                )}
                                className="task-list-item-copy-icon-container cursor-pointer"
                              >
                                {isFieldCopied(taskId, 'subject') ? (
                                  <Check
                                    width={16}
                                    height={16}
                                    color="#6b7280"
                                  />
                                ) : (
                                  <Copy
                                    width={16}
                                    height={16}
                                    color="#6b7280"
                                  />
                                )}
                              </div>
                            </OverlayTooltip>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {(hasMessageContent ||
                    hasAttachments ||
                    isShareableApiLoading) && (
                    <div className="d-flex flex-column gap-4">
                      <div className="d-flex align-items-center justify-content-between">
                        {isShareableApiLoading ? (
                          <SkeletonLoading width={123} height={16} />
                        ) : (
                          <span className="semibold-1 gray-txt-15">
                            {getTaskMessageLabel(sequenceStepType)}
                          </span>
                        )}
                      </div>
                      <div className="task-list-item-message-wrapper">
                        <div
                          ref={(el) => {
                            messageRefs.current[taskId] = el;
                          }}
                          className={classNames(
                            'task-list-item-message-scrollable regular-2 popover-arrow-color-txt',
                            {
                              'task-list-item-message-html': shouldRenderMessageAsHtml,
                            },
                          )}
                        >
                          {isShareableApiLoading ? (
                            <div className="d-flex flex-column gap-4">
                              <SkeletonLoading width={536} height={14} />
                              <SkeletonLoading width={423} height={14} />
                              <SkeletonLoading width={299} height={14} />
                            </div>
                          ) : (
                            <>
                              {hasMessageContent && (
                                <>
                                  {shouldRenderMessageAsHtml ? (
                                    <span
                                      className="regular-2 popover-arrow-color-txt"
                                      dangerouslySetInnerHTML={{
                                        __html: messageContent || '',
                                      }}
                                    />
                                  ) : (
                                    messageContent
                                  )}
                                </>
                              )}
                              {renderAttachments(task, shareableData)}
                            </>
                          )}
                        </div>
                        {!isShareableApiLoading && hasMessageContent && (
                          <div className="task-list-item-copy-icon-static d-flex align-items-center justify-content-end">
                            <OverlayTooltip
                              text={
                                isFieldCopied(taskId, 'message')
                                  ? 'Copied'
                                  : 'Copy'
                              }
                              placement={Placement.Bottom}
                            >
                              <div
                                {...accessibleOnClick(() =>
                                  handleCopyContent(taskId, 'message'),
                                )}
                                className="task-list-item-copy-icon-container cursor-pointer"
                              >
                                {isFieldCopied(taskId, 'message') ? (
                                  <Check
                                    width={16}
                                    height={16}
                                    color="#6b7280"
                                  />
                                ) : (
                                  <Copy
                                    width={16}
                                    height={16}
                                    color="#6b7280"
                                  />
                                )}
                              </div>
                            </OverlayTooltip>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="d-flex flex-column gap-4 add-task-note-container">
                    {isShareableApiLoading ? (
                      <SkeletonLoading width={123} height={16} />
                    ) : showNoteEditor === taskId ||
                      Boolean(shareableData?.noteResponse?.id) ? (
                      <span className="semibold-1 gray-txt-15">Note</span>
                    ) : (
                      <div
                        className="add-task-note"
                        {...accessibleOnClick(() => {
                          setShowNoteEditor(taskId);
                          setEditingNoteTaskId(taskId);
                        })}
                      >
                        <Plus width={16} height={16} />
                        <span>Add Task Note</span>
                      </div>
                    )}
                    {(showNoteEditor === taskId ||
                      Boolean(shareableData?.noteResponse?.id)) && (
                      <InlineMentionEditor
                        key={`task-editor-${taskId}-${
                          shareableData?.noteResponse?.id || 'new'
                        }-${taskVisibilityKeys[taskId]}`}
                        value={
                          shareableData?.noteResponse?.content ||
                          taskEditorValues[taskId] ||
                          ''
                        }
                        onEditorChange={(value) =>
                          handleTaskEditorChange(taskId, value)
                        }
                        showAddAttachments={true}
                        showToolBarContainer={true}
                        showFooterToolbarContainer={
                          !shareableData?.noteResponse?.id ||
                          editingNoteTaskId === taskId
                        }
                        showFooterVisibilitySelector={
                          !shareableData?.noteResponse?.id ||
                          editingNoteTaskId === taskId
                        }
                        showFooterSaveButton={
                          !shareableData?.noteResponse?.id ||
                          editingNoteTaskId === taskId
                        }
                        disabled={
                          !(
                            !shareableData?.noteResponse?.id ||
                            editingNoteTaskId === taskId
                          )
                        }
                        showDiscardButton={
                          shareableData?.noteResponse?.content ||
                          taskEditorValues[taskId]
                        }
                        onDiscard={() =>
                          handleDiscard(
                            taskId,
                            Boolean(shareableData?.noteResponse?.id),
                          )
                        }
                        onFooterSave={() => {
                          handleTaskNoteSave(taskId);
                          // After save click, switch back to view mode (footer hidden).
                          setEditingNoteTaskId(null);
                        }}
                        onFooterCancel={() => setEditingNoteTaskId(null)}
                        onEditClick={() => {
                          setShowNoteEditor(taskId);
                          setEditingNoteTaskId(taskId);
                        }}
                        showEditIcon={true}
                        selectedVisibilityKey={
                          taskVisibilityKeys[taskId] ||
                          shareableData?.noteResponse?.visibility ||
                          visibility.Public
                        }
                        onVisibilityChange={(key) =>
                          handleTaskVisibilityChange(taskId, key)
                        }
                        isFooterSaveButtonDisabled={
                          (!taskEditorValues[taskId]?.trim() &&
                            !shareableData?.noteResponse?.content) ||
                          getIsRequestPending(
                            createProspectNoteRequestStatus,
                          ) ||
                          getIsRequestPending(updateProspectNoteRequestStatus)
                        }
                        // mergeTags={MENTION_ARRAY}
                        placeholder={
                          (shareableData?.noteResponse?.visibility ||
                            taskVisibilityKeys[taskId] ||
                            visibility.Public) === visibility.Private
                            ? 'Visible only to you and people tagged here'
                            : 'Visible to everyone in the organisation*'
                        }
                        saveButtonText={
                          shareableData?.noteResponse ? 'Save' : 'Add Note'
                        }
                        attachments={
                          taskAttachments[taskId] ||
                          (shareableData?.noteResponse?.attachments
                            ? transformApiAttachments(
                                shareableData.noteResponse.attachments,
                              )
                            : [])
                        }
                        onAttachmentsChange={(attachments) =>
                          handleTaskAttachmentsChange(taskId, attachments)
                        }
                        onAttachmentStatusChange={(attachment) =>
                          handleTaskAttachmentStatusChange(taskId, attachment)
                        }
                        sendAttachmentUploadRequest={
                          handleSendAttachmentUploadRequest
                        }
                        uploadAttachmentRequestStatus={
                          uploadAttachmentRequestStatus
                        }
                        uploadAttachmentRequestError={
                          uploadAttachmentRequestError
                        }
                        uploadedAttachment={uploadedAttachment}
                        resetUploadAttachmentRequest={
                          handleResetUploadAttachmentRequest
                        }
                        sendDeleteAttachmentRequest={
                          handleSendDeleteAttachmentRequest
                        }
                        deleteAttachmentRequestStatus={
                          deleteAttachmentRequestStatus
                        }
                        deleteAttachmentRequestError={
                          deleteAttachmentRequestError
                        }
                        deletedAttachmentId={deletedAttachmentId}
                        resetDeleteAttachmentRequest={
                          handleResetDeleteAttachmentRequest
                        }
                        onAttachmentsMetaChange={() => {}}
                        showSkeleton={isShareableApiLoading}
                      />
                    )}
                  </div>

                  {!shouldHideActions && (
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-12">
                        {isShareableApiLoading ? (
                          <SkeletonLoading width={32} height={32} />
                        ) : (
                          <OverlayTooltip
                            placement={Placement.Bottom}
                            text={`${
                              shareableData?.isAborted
                                ? 'Task is aborted'
                                : 'Skip'
                            }`}
                          >
                            <div
                              className={classNames('task-tab-skip', {
                                disabled:
                                  isTaskActionDisabled ||
                                  editingNoteTaskId === taskId,
                              })}
                              {...accessibleOnClick(() => {
                                if (
                                  isDemoAccount &&
                                  editingNoteTaskId !== taskId
                                ) {
                                  setGlobalRestrictionModalContent({
                                    title:
                                      'Skipping tasks is disabled in demo mode',
                                    content:
                                      'This is a demo account. Signup to manage your real tasks in Saleshandy.',
                                  });
                                  setShowGlobalRestrictionModal(true);
                                  return;
                                }
                                if (
                                  getIsRequestPending(updateTaskRequestStatus)
                                ) {
                                  return;
                                }
                                handleTaskAction(
                                  task,
                                  TaskAction.SkipTask,
                                  isTaskActionDisabled ||
                                    editingNoteTaskId === taskId,
                                );
                              })}
                            >
                              <ForwardStep
                                width={20}
                                height={20}
                                className={`${
                                  isTaskActionDisabled
                                    ? 'cursor-not-allowed'
                                    : 'pointer'
                                }`}
                              />
                            </div>
                          </OverlayTooltip>
                        )}
                        {!isAutomationTask && isShareableApiLoading ? (
                          <SkeletonLoading width={32} height={32} />
                        ) : !isAutomationTask ? (
                          <OverlayTooltip
                            text={`${
                              shareableData?.isAborted
                                ? 'Task is aborted'
                                : 'Snooze'
                            }`}
                            placement={Placement.Bottom}
                            className="cell-card__tooltip"
                          >
                            <div
                              className={classNames(
                                'icon-container task-tab-snooze',
                                {
                                  disabled:
                                    isTaskActionDisabled ||
                                    editingNoteTaskId === taskId,
                                },
                              )}
                              {...accessibleOnClick(() => {
                                if (
                                  isDemoAccount &&
                                  editingNoteTaskId !== taskId
                                ) {
                                  setGlobalRestrictionModalContent({
                                    title:
                                      'Snoozing tasks is disabled in demo mode',
                                    content:
                                      'This is a demo account. Signup to snooze tasks and manage your follow-ups effectively.',
                                  });
                                  setShowGlobalRestrictionModal(true);
                                  return;
                                }
                                if (
                                  getIsRequestPending(updateTaskRequestStatus)
                                ) {
                                  return;
                                }
                                handleTaskAction(
                                  task,
                                  TaskAction.SnoozeTask,
                                  isTaskActionDisabled ||
                                    editingNoteTaskId === taskId,
                                );
                              })}
                            >
                              <Alarm
                                width={20}
                                height={20}
                                className={`${
                                  isTaskActionDisabled
                                    ? 'cursor-not-allowed'
                                    : 'pointer'
                                }`}
                              />
                            </div>
                          </OverlayTooltip>
                        ) : null}
                      </div>

                      <div className="d-flex align-items-center gap-8">
                        {isAutomationTask && ((task as any)?.failedReason || shareableData?.linkedInTaskError?.errorCode || (task as any)?.linkedInTaskError?.errorCode) ? (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              if (onTaskAction) {
                                onTaskAction(
                                  Number(task.id),
                                  TaskAction.RetryLinkedInTask,
                                );
                              }
                            }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5, flexShrink: 0 }}>
                              <polyline points="23 4 23 10 17 10" />
                              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                            </svg>
                            Retry
                          </Button>
                        ) : isAutomationTask ? null : isShareableApiLoading ? (
                          <SkeletonLoading width={114} height={32} />
                        ) : shareableData?.isAborted ? (
                          <OverlayTooltip
                            placement={Placement.Bottom}
                            text="Task is aborted"
                          >
                            <Button
                              variant="secondary"
                              className="task-tab-complete"
                              disabled={
                                isTaskActionDisabled ||
                                editingNoteTaskId === taskId
                              }
                              {...accessibleOnClick(() => {
                                if (isDemoAccount) {
                                  setGlobalRestrictionModalContent({
                                    title:
                                      'Completing tasks is disabled in demo mode',
                                    content:
                                      'This is a demo account. Signup to track and complete real tasks in Saleshandy.',
                                  });
                                  setShowGlobalRestrictionModal(true);
                                  return;
                                }
                                if (
                                  getIsRequestPending(updateTaskRequestStatus)
                                ) {
                                  return;
                                }
                                handleTaskAction(
                                  task,
                                  TaskAction.ExecuteTask,
                                  isTaskActionDisabled ||
                                    editingNoteTaskId === taskId,
                                );
                              })}
                            >
                              <Check width={16} height={16} className="mr-2" />
                              Complete
                            </Button>
                          </OverlayTooltip>
                        ) : (
                          <Button
                            variant="secondary"
                            className="task-tab-complete"
                            disabled={
                              isTaskActionDisabled ||
                              editingNoteTaskId === taskId
                            }
                            {...accessibleOnClick(() => {
                              if (isDemoAccount) {
                                setGlobalRestrictionModalContent({
                                  title:
                                    'Completing tasks is disabled in demo mode',
                                  content:
                                    'This is a demo account. Signup to track and complete real tasks in Saleshandy.',
                                });
                                setShowGlobalRestrictionModal(true);
                                return;
                              }
                              if (
                                getIsRequestPending(updateTaskRequestStatus)
                              ) {
                                return;
                              }
                              handleTaskAction(
                                task,
                                TaskAction.ExecuteTask,
                                isTaskActionDisabled ||
                                  editingNoteTaskId === taskId,
                              );
                            })}
                          >
                            <Check width={16} height={16} className="mr-2" />
                            Complete
                          </Button>
                        )}

                        {isShareableApiLoading ? (
                          <SkeletonLoading width={150} height={32} />
                        ) : (
                          <>
                            {isLinkedInType(sequenceStepType) && (
                              <Button
                                className=""
                                onClick={() => {
                                  if (isDemoAccount) {
                                    let content = {
                                      title:
                                        'Viewing LinkedIn profiles is disabled in demo mode',
                                      content:
                                        'This is a demo account. Signup to perform tasks in real account.',
                                    };

                                    if (
                                      sequenceStepType ===
                                      SequenceStepType.LinkedInConnectionRequest
                                    ) {
                                      content = {
                                        title: 'Send Connection Request',
                                        content:
                                          'This is a demo account with sample data. Signup to perform tasks in real account.',
                                      };
                                    }
                                    if (
                                      sequenceStepType ===
                                      SequenceStepType.LinkedInMessage
                                    ) {
                                      content = {
                                        title:
                                          'Sending Message is disabled in demo mode',
                                        content:
                                          'This is a demo account. Signup to perform tasks in real account.',
                                      };
                                    }
                                    if (
                                      sequenceStepType ===
                                      SequenceStepType.LinkedInInMail
                                    ) {
                                      content = {
                                        title:
                                          'Sending InMail is disabled in demo mode',
                                        content:
                                          'This is a demo account. Signup to perform tasks in real account.',
                                      };
                                    }

                                    setGlobalRestrictionModalContent(content);
                                    setShowGlobalRestrictionModal(true);
                                    return;
                                  }
                                  sendLinkedinRequest(task, shareableData);
                                }}
                                disabled={
                                  !getProspectDetails(
                                    shareableData?.prospect?.attributes
                                      ? shareableData.prospect.attributes
                                      : (task as any)?.prospect?.attributes,
                                  )?.LinkedIn || editingNoteTaskId === taskId
                                }
                              >
                                {getButtonLabel(sequenceStepType)}
                              </Button>
                            )}
                          </>
                        )}

                        {isShareableApiLoading ? (
                          <SkeletonLoading width={150} height={32} />
                        ) : (
                          renderWhatsAppButton(
                            task,
                            shareableData,
                            editingNoteTaskId === taskId,
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {isLoadingMore && (
          <div className="task-list-loading d-flex justify-content-center">
            <Spinner className="spinner--blue" animation="border" />
          </div>
        )}
        {hasMoreTasks === false && resolvedTasks?.length > 20 && (
          <div className="end-list-divider">
            <span>The End</span>
          </div>
        )}
      </div>

      {/* Snooze Modal */}
      {isSnoozeModalVisible && (
        <SnoozeModal
          show={isSnoozeModalVisible}
          onClose={() => {
            setIsSnoozeModalVisible(false);
            setSelectedTask(null);
            setTaskAction(null);
          }}
          onSubmit={handleSnooze}
          isRequestPending={isActionPending}
          bulkAction={false}
        />
      )}

      {/* Confirmation Modal for Skip and Complete */}
      {isConfirmationModalVisible && taskAction !== null && (
        <ConfirmationModalV3
          show={isConfirmationModalVisible}
          onSubmit={handleConfirm}
          onClose={() => {
            setIsConfirmationModalVisible(false);
            setSelectedTask(null);
            setTaskAction(null);
            setCallOutcome(null);
          }}
          showCloseIcon={false}
          hideTitleIcon={true}
          icon={
            taskAction === TaskAction.ExecuteTask ? (
              <CircleCheck width={24} height={24} className="orange-txt-14" />
            ) : (
              <ForwardStep width={24} height={24} className="orange-txt-14" />
            )
          }
          title={getTaskActionTitle(taskAction, 1)}
          cancelButtonText="No"
          submitButtonText={
            taskAction === TaskAction.SkipTask ? 'Skip Anyway' : 'Yes'
          }
          content={getModalContent()}
          isSubmitLoading={isActionPending}
          isSubmitDisabled={isButtonDisabled() || isActionPending}
          className={`task-confirmation-modal ${getConfirmationModalClass(
            taskAction,
            resolveSequenceStepType(selectedTask?.type) ??
              SequenceStepType.Custom,
            [],
          )}`}
        />
      )}
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title={globalRestrictionModalContent?.title}
        content={globalRestrictionModalContent?.content}
        primaryBtnLabel="Sign up"
      />
      {failedReasonTask && (
        <FailedReasonPopup
          task={failedReasonTask}
          show={Boolean(failedReasonTask)}
          onClose={() => setFailedReasonTask(null)}
        />
      )}
    </div>
  );
};

export default TaskList;
